import { useParams } from "react-router-dom";
import {
  ReferenceOption,
  ReferenceSelect,
} from "src/components/ui/ReferenceSelect";
import { useGetDevices } from "src/hooks/queries";
import { useGetQualityEventDocumentReferences } from "src/hooks/queries/quality-events";
import {
  isQualityEventDocumentReference,
  QualityEventDocumentReference,
  QualityEventField,
  QualityEventValue,
} from "src/types/quality-events";

type ReferenceProps = {
  field: QualityEventField;
  value: QualityEventValue | undefined;
  onChange?: (value: QualityEventValue) => void;
  disabled?: boolean;
  required?: boolean;
  outstanding?: string;
  invalid?: boolean;
};

const QualityEventReferenceField = ({
  field,
  value,
  onChange,
  disabled,
  required,
  outstanding,
  invalid,
  options,
  isLoading,
  isError,
  resource,
}: ReferenceProps & {
  options: ReferenceOption<string | QualityEventDocumentReference>[];
  isLoading: boolean;
  isError: boolean;
  resource: "devices" | "documents";
}) => {
  const fallbackOption = isQualityEventDocumentReference(value)
    ? {
        id: value.documentVersionId ?? value.documentId,
        label: value.displaySnapshot,
        value,
      }
    : typeof value === "string" && value
      ? { id: value, label: value, value }
      : null;
  const selectedOption = fallbackOption?.id
    ? (options.find((option) => option.id === fallbackOption.id) ??
      fallbackOption)
    : null;
  const resourceLabel =
    resource === "documents" ? "document references" : resource;
  let helperText = field.tooltip;
  if (isError) helperText = `Unable to load ${resourceLabel}.`;
  else if (isLoading) helperText = `Loading ${resource}…`;
  else if (!options.length) helperText = `No ${resourceLabel} available.`;
  const withTitle = (
    option: ReferenceOption<string | QualityEventDocumentReference>
  ) => ({
    ...option,
    title:
      resource === "documents"
        ? option.label.match(/-R\d+-([\s\S]+)$/)?.[1]
        : undefined,
  });
  return (
    <ReferenceSelect
      label={field.label}
      value={selectedOption ? withTitle(selectedOption) : null}
      onChange={onChange}
      disabled={disabled}
      required={required}
      outstanding={outstanding}
      invalid={invalid}
      options={options.map(withTitle)}
      isLoading={isLoading}
      isError={isError}
      helperText={helperText}
      noOptionsText={`No matching ${resource}`}
    />
  );
};

export const QualityEventDeviceField = ({
  orgId,
  ...props
}: ReferenceProps & { orgId: string }) => {
  const { data = [], isLoading, isError } = useGetDevices({ orgId });
  const options = data.map(({ id, name }) => ({ id, label: name, value: id }));
  return (
    <QualityEventReferenceField
      {...props}
      resource="devices"
      options={options}
      isLoading={isLoading}
      isError={isError}
    />
  );
};

export const QualityEventDocumentReferenceInput = (props: ReferenceProps) => {
  const { orgId = "" } = useParams();
  const {
    data = [],
    isLoading,
    isError,
  } = useGetQualityEventDocumentReferences({
    orgId,
  });
  const options = data.map((reference) => ({
    id: reference.documentVersionId ?? reference.documentId,
    label: reference.displaySnapshot,
    value: reference,
  }));
  return (
    <QualityEventReferenceField
      {...props}
      resource="documents"
      options={options}
      isLoading={isLoading}
      isError={isError}
    />
  );
};
